import type {
  FileProcessingStatus,
  FileSource,
  WalkthroughClipResponse,
} from "@patent-studio/api-types";
import { blank } from "@/lib/figure-status";
import type { WalkthroughClip, WalkthroughClipState } from "./WalkthroughCard";

/**
 * The card's reading of a clip's stored status. Every status outside the
 * transcribe arc reads as plainly stored: a clip from before transcription
 * existed must not claim to be on its way anywhere.
 */
function clipState(status: FileProcessingStatus): WalkthroughClipState {
  switch (status) {
    case "transcribe_pending":
    case "transcribe_processing":
      return "transcribing";
    case "transcribe_complete":
      return "transcribed";
    case "transcribe_failed":
      return "failed";
    default:
      return "stored";
  }
}

/**
 * A finished clip's state once what it heard is weighed. A clip that heard no
 * words has nothing to fold in, and saying it is transcribed would leave the
 * attorney looking for words that never come. Adopted outranks transcribed:
 * words already in the field must not fold in again, and the row owes the
 * reader a receipt, not an offer.
 */
function heldState(
  read: WalkthroughClipState,
  transcript: string | null,
  adopted: boolean,
): WalkthroughClipState {
  if (read !== "transcribed") return read;
  if (blank(transcript ?? "")) return "silent";
  return adopted ? "adopted" : "transcribed";
}

export function clipKindFromSource(
  source: FileSource | undefined | null,
): WalkthroughClipResponse["kind"] {
  return source === "recording" ? "recording" : "upload";
}

/**
 * The clips this figure holds, taken from the figures GET projection.
 *
 * Ids on the definition that name no projected clip are dropped rather than
 * listed: the write refuses ids that are not walk-through clips this task
 * holds, and a row that survived a delete would otherwise show a clip nobody
 * can open.
 */
export function clipsHeldByFigure(
  clips: readonly WalkthroughClipResponse[],
  ids: readonly string[] | undefined,
  adoptedIds?: readonly string[],
): WalkthroughClip[] {
  const held = new Map(clips.map((clip) => [clip.file_id, clip]));
  const adopted = new Set(adoptedIds ?? []);
  return (ids ?? []).flatMap((id) => {
    const clip = held.get(id);
    if (clip === undefined) return [];
    const transcript = clip.transcript ?? null;
    return [
      {
        fileId: id,
        kind: clip.kind,
        filename: clip.filename,
        state: heldState(clipState(clip.status), transcript, adopted.has(id)),
        transcript,
        sentAt: clip.dispatched_at ?? null,
      },
    ];
  });
}
